import React from 'react';
import { buildShiftTooltip, buildOfferTooltip, buildPaycodeTooltip } from '../Utils/uiGenerators';
import {
    RequestStatus, RequestSource, RequestTypes, ShiftSegmentTypeName, RequestSubtypes,
} from '../../../Enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faTimesCircle, faShareSquare, faCheckSquare, faArrowAltFromLeft, faArrowAltFromRight, faAlarmExclamation } from '@fortawesome/pro-light-svg-icons';
import { renderToStaticMarkup } from 'react-dom/server';
import moment from 'moment';
import './calendar.scss';
import { AssignmentTurnedIn, CompareArrows as CompareArrowsIcon } from '@mui/icons-material';

const statusDictionary = {
    [RequestStatus.Offered]: {
        value: 'Offered',
        color: '#b68cdb'
    },
    [RequestStatus.Submitted]: {
        value: 'Submitted',
        color: '#9159c2'
    },
    [RequestStatus.Approved]: {
        value: 'Approved',
        color: '#07ad34'
    },
    [RequestStatus.Refused]: {
        value: 'Refused',
        color: '#ff4040'
    },
    [RequestStatus.OfferRetracted]: {
        value: 'Retracted',
        color: '#8a37d4'
    },
    [RequestStatus.RequestRetracted]: { // NOT USED ATM
        value: 'Retracted',
        color: '#8a37d4'
    },
    [RequestStatus.Expired]: {
        value: 'Expired',
        color: '#ff572e'
    },
    [RequestStatus.Pending]: {
        value: 'Pending',
        color: '#fff000'
    },
    [RequestStatus.Declined]: {
        value: 'Declined',
        color: '#ff4040'
    },
    [RequestStatus.AutoRetracted]: {
        value: 'Auto-retracted',
        color: '#8a37d4'
    },
    [RequestStatus.RetractedSubmitted]: {
        value: 'Retracted submission',
        color: '#8a37d4'
    },
}

const statusIcon = (status, hasProposals = false) => {
    let icon;
    switch (status) {
        case RequestStatus.Offered:
            if (hasProposals) {
                icon = renderToStaticMarkup(<FontAwesomeIcon icon={faArrowAltFromRight} />)
            } else {
                icon = renderToStaticMarkup(<FontAwesomeIcon icon={faArrowAltFromLeft} />)
            }
            break;
        case RequestStatus.Submitted:
        case RequestStatus.Pending:
            icon = renderToStaticMarkup(<FontAwesomeIcon icon={faShareSquare} />)
            break;
        case RequestStatus.Approved:
            icon = renderToStaticMarkup(<FontAwesomeIcon icon={faCheckSquare} />)
            break;
        case RequestStatus.Refused:
            icon = renderToStaticMarkup(<FontAwesomeIcon icon={faTimesCircle} />)
            break;
        case RequestStatus.OfferRetracted:
        case RequestStatus.RequestRetracted:
            icon = renderToStaticMarkup(<FontAwesomeIcon icon={faHistory} />)
            break;
        case RequestStatus.Expired:
            icon = renderToStaticMarkup(<FontAwesomeIcon icon={faAlarmExclamation} />)
            break;
        default:
            icon = '';
    }
    return icon;
}

/**
 * @see https://github.com/moment/moment/issues/1048
 * @param {number} durationInSeconds
 * @returns {string} Time as `H:mm` amount of hours in the period
 * even when higher than 24H. `Ex.: 25:30`
 */
export const getFormattedPaycodeDuration = (durationInSeconds) => {

    if (!durationInSeconds) {
        return '0:00';
    }

    const duration = moment.duration(durationInSeconds, 'seconds');
    const hours = Math.floor(moment.duration(durationInSeconds, 'seconds').asHours());
    const ms = duration.asMilliseconds();
    const fullDuration = moment.utc(ms).format('H:mm');
    const durationMinutes = moment.utc(ms).format(':mm');
    return hours > 24 ? `${hours}${durationMinutes}` : fullDuration;
};

export const renderSchedule = (schedule, isShowOrganizationalPath) => {
    switch (schedule.calendarId) {
        case '0': // Own schedule
            return renderToStaticMarkup(
                <>
                    <div className="calendar-schedule--card-wrapper">
                        <div className="schedule-card-title">
                            {schedule.title}
                            {isShowOrganizationalPath
                                && (
                                    <span className="schedule-card-org-path">
                                        {getOrgPath(schedule)}
                                    </span>
                                )
                            }
                        </div>
                        <input
                            type="checkbox"
                            className="schedule-card-checkbox"
                            defaultChecked={schedule.raw.isChecked}
                        />
                    </div>
                    <span className="tooltiptext tooltiptext-top">
                        <span className="tooltip-arrow tooltip-bottom-arrow"></span>
                        {buildShiftTooltip(schedule.raw)}
                    </span>
                </>);
        case '1': // Paycode
            const paycode = schedule.raw;
            const formattedDuration = getFormattedPaycodeDuration(paycode.durationInTime);
            const title = `${paycode.payCodeRef.qualifier} [${formattedDuration}]`;

            return renderToStaticMarkup(
                <>
                    <div className="calendar-schedule--card-wrapper default-cursor">
                        <div className="schedule-card-title">{title}</div>
                    </div>
                    <span className="tooltiptext tooltiptext-top">
                        <span className="tooltip-arrow tooltip-bottom-arrow"></span>
                        {buildPaycodeTooltip(paycode, formattedDuration)}
                    </span>
                </>);
        default:
            if (schedule.raw.isAvailableOffer) {
                if (schedule.raw.source === RequestSource.Requester) {
                    if (schedule.raw.type === RequestTypes.openShift) {
                        const isOpenShiftSignUp = schedule.raw.requestSubtype === RequestSubtypes.signUp;
                        const hasTransfer = schedule.raw.shifts[0].segments?.some(segment => segment.type === ShiftSegmentTypeName.TRANSFER_SEGMENT);
                        // Available open shifts
                        return renderToStaticMarkup(
                            <>
                                <div className="calendar-schedule--card-wrapper">
                                    <div className="schedule-card-title">
                                        <span className="schedule-card-availability">
                                            {schedule.raw.available}
                                        </span>
                                        {schedule.title}
                                        {isShowOrganizationalPath
                                            && (
                                                <span className="schedule-card-org-path">
                                                    {getOrgPath(schedule)}
                                                </span>
                                            )
                                        }
                                    </div>
                                    <div>
                                        {isOpenShiftSignUp && (
                                            <AssignmentTurnedIn className="signUpIconSize"/>
                                        )}
                                        {hasTransfer ? <CompareArrowsIcon className="transferIconSize"/> : <></>}
                                        <input
                                            type="checkbox"
                                            className="schedule-card-checkbox"
                                            defaultChecked={schedule.raw.isChecked}
                                        />
                                    </div>
                                    
                                </div>
                                <span className="tooltiptext tooltiptext-top">
                                    <span className="tooltip-arrow tooltip-bottom-arrow"></span>
                                    {buildShiftTooltip(schedule.raw.shifts[0], true, schedule.raw.available, isOpenShiftSignUp, hasTransfer)}
                                </span>
                            </>);
                    }
                    if (schedule.raw.type === RequestTypes.voluntaryTimeOff) {
                        const isSignUp = schedule.raw.requestSubtype === RequestSubtypes.signUp;
                        return renderToStaticMarkup(<>
                            <div className="calendar-schedule--card-wrapper">
                                {schedule.title}
                                {isSignUp && (
                                    <div>
                                        <AssignmentTurnedIn className="signUpIconSize"/>
                                    </div>
                                )}
                            </div>
                            <span className="tooltiptext tooltiptext-top">
                                <span className="tooltip-arrow tooltip-bottom-arrow"></span>
                                {buildOfferTooltip(schedule, 'New', isSignUp)}
                            </span>
                        </>);
                    }
                }
                return '<div class="calendar-schedule--card-wrapper"><div class="schedule-card-title">'
                    + schedule.title
                    + (isShowOrganizationalPath
                        ? '<span class="schedule-card-org-path">' + getOrgPath(schedule) + '</span>'
                        : '')
                    + '</div></div><span class="tooltiptext tooltiptext-top"><span class="tooltip-arrow tooltip-bottom-arrow"></span>'
                    + renderToStaticMarkup(buildOfferTooltip(schedule, statusDictionary[schedule.raw.status].value)) + '</span>'
            }
            else {
                let hasProposals = false;
                if (schedule.raw.proposals && schedule.raw.proposals.length > 0 && schedule.raw.status !== RequestStatus.Pending) {
                    let statusFilter = [
                        RequestStatus.Refused,
                        RequestStatus.OfferRetracted,
                        RequestStatus.Expired,
                        RequestStatus.Declined,
                        RequestStatus.AutoRetracted
                    ];

                    if (schedule.raw.isProposedByOfferer) {
                        statusFilter.push(0);
                    }
                    if (schedule.raw.proposals.filter(proposal =>
                        !statusFilter.includes(proposal.status)).length > 0
                        && schedule.raw.source === RequestSource.Offerer) {
                        hasProposals = true
                    } else if (schedule.raw.source === RequestSource.Requester
                        && schedule.raw.isOwnProposals) {
                        let proposal = schedule.raw.proposals[0]
                        return '<span style="color:' + schedule.color + '">'
                            + statusIcon(proposal.status)
                            + '</span> ' + schedule.title + ' <span class="tooltiptext tooltiptext-top"><span class="tooltip-arrow tooltip-bottom-arrow"></span>'
                            + renderToStaticMarkup(buildOfferTooltip(schedule, statusDictionary[proposal.status].value)) + '</span>'
                    }
                }
                return '<span style="color:' + schedule.color + '">' + statusIcon(schedule.raw.status, hasProposals)
                    + '</span> ' + schedule.title + ' <span class="tooltiptext tooltiptext-top"><span class="tooltip-arrow tooltip-bottom-arrow"></span>'
                    + renderToStaticMarkup(buildOfferTooltip(schedule, statusDictionary[schedule.raw.status].value)) + '</span>'
            }
    }
}

const getOrgPath = (schedule) => {
    const { shift, shifts, segments } = schedule.raw;

    let orgPath = '';
    let shiftSegments = [];
    if (shift) {
        shiftSegments = shift.segments;
    } else if (shifts) {
        shiftSegments = schedule.raw.shifts[0].segments;
    } else if (segments) {
        shiftSegments = schedule.raw.segments;
    }

    for (const segment of shiftSegments) {
        if (segment.segmentTypeRef.qualifier === ShiftSegmentTypeName.REGULAR_SEGMENT) {
            return segment.shortOrgJobPath;
        }
        if (segment.segmentTypeRef.qualifier === ShiftSegmentTypeName.TRANSFER_SEGMENT) {
            orgPath = segment.shortOrgJobPath;
        }
    }

    return orgPath;
}